import { ReactNode } from "react";
import ModulesSidebar from "./modules-sidebar.js";
import { Button } from "./button/button.js";
import { ArrowLongRightIcon } from "@heroicons/react/24/outline";

type Props = {
  title?: string | null;
  isOptional?: boolean;
  footerActions?: ReactNode;
  nextItem: { title?: string | null } | null;
  nextItemLink: string | null;
  isCurrentCompleted: boolean;
  children: ReactNode;
};

export default function LearningItemLayout({
  title,
  isOptional,
  footerActions,
  nextItem,
  nextItemLink,
  isCurrentCompleted,
  children,
}: Props) {
  const hasNext = Boolean(nextItem && nextItemLink);

  return (
    <div className={`flex flex-col gap-6 lg:flex-row lg:items-start ${hasNext ? "pb-8 lg:pb-0" : ""}`}>
      <div className="min-w-0 flex-1 rounded-xl border border-gray bg-white/60 p-6 shadow-lg shadow-lac-leman/10 backdrop-blur-xl sm:p-8 lg:p-12">
        {(title || isOptional) && (
          <div className="mb-6 flex flex-col-reverse flex-wrap items-start justify-between gap-0 md:flex-row md:items-center lg:items-center">
            {title ? (
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-semibold md:text-3xl">{title}</h1>
              </div>
            ) : null}
            {isOptional ? (
              <div className="mb-2 flex flex-wrap items-center justify-end md:mb-0">
                <span className="text-gray-500 ml-auto inline-flex shrink-0 items-center self-center rounded-full border border-gray px-2 py-1 text-[9px] font-semibold uppercase leading-none tracking-[0.15em]">
                  Optional
                </span>
              </div>
            ) : null}
          </div>
        )}
        {children}
        {footerActions ? <div className="mt-8">{footerActions}</div> : null}
      </div>

      <aside className="lg:sticky lg:top-6 lg:w-[320px]">
        <div>
          {nextItem && nextItemLink ? (
            <div
              id="next-item"
              className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray bg-white/80 shadow-lg backdrop-blur-xl lg:relative lg:bottom-auto lg:left-auto lg:right-auto lg:z-auto lg:mb-4 lg:overflow-hidden lg:rounded-xl lg:border lg:bg-white/60 lg:shadow-lac-leman/10">
              <div className="hidden border-b border-gray px-4 py-3 backdrop-blur lg:block">
                <p className="font-semibold">Up next</p>
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-gray-500 text-xs font-medium lg:hidden">Up next</p>
                  <span className="block truncate text-sm font-medium">{nextItem.title ?? "Next item"}</span>
                </div>
                <Button
                  to={nextItemLink}
                  filled={isCurrentCompleted ? "lac-leman" : undefined}
                  className="group min-w-fit p-2">
                  <span className="flex items-center gap-2">
                    <ArrowLongRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                </Button>
              </div>
            </div>
          ) : null}
          <div className="hidden overflow-hidden rounded-xl border border-gray bg-white/60 shadow-lg shadow-lac-leman/10 backdrop-blur-xl lg:block">
            <div className="max-h-[70vh] overflow-y-auto">
              <ModulesSidebar />
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
